import { BookingFormValues } from "../types/overview";
import { PaymentStatusRequest } from "../types/payment";
import { OrderDetailResponse } from "../types/order";
import { ApproveDocumentReqType, RequploadDocumentReqType, UpdateSingleProductReqType, UpdateTCSReqType } from "../types/type.api";
import {
  decideForexLines,
  decideForexPayment,
  getForexOrderDetails,
  listForexOrderFiles,
  recordForexPayment,
} from "service/moneeflo/legacy-forex-order-api";
import {
  assignForexCard,
  approveForexKyc,
  getForexKycHistory,
  markLrsSigned,
  rejectForexDocument,
  requestForexDocument,
  uploadForexOrderDocument,
  verifyForexDocuments,
  updateForexLine,
  listCustomerAddresses,
  listForexCards,
  setForexNotes,
  setForexServiceCharge,
  setForexTcs,
} from "service/moneeflo/legacy-forex-desk-api";
import { fileFromDataUrl } from "service/moneeflo/legacy-order-api";
import { notAvailable } from "service/moneeflo/not-available";
import {
  getGenericOrderDetails as loadGenericOrder,
  updateGenericOrderStatus as moveGenericOrder,
} from "service/moneeflo/legacy-generic-order-api";

/*
 * Every call on the forex order screen used to go to Xano, which the browser
 * now blocks. Each one below either goes to an endpoint the new backend has
 * (the order, its payments and documents, and the desk's actions on it) or,
 * for what is left of the generic orders, says plainly that it is not
 * available yet. None of them reports success it did not achieve. See service/moneeflo/legacy-forex-order-api.ts and
 * legacy-forex-desk-api.ts for what is answered, and why the rest is not.
 */

type StatusWithoutName = Omit<BookingFormValues["status"][number], "name">;

type BookingFormPayload = {
    order_id: number;
    status: StatusWithoutName[];
};

/* ------------------------------------------------- answered by core ---- */

export const getOrderDetails = async (orderId: string, options?: { readChecks?: boolean }): Promise<OrderDetailResponse> => {
  return (await getForexOrderDetails(orderId, options)) as unknown as OrderDetailResponse;
}

export const capturePayment = async (data: Parameters<typeof recordForexPayment>[0]) => {
  return await recordForexPayment(data);
}

export const getAllFiles = async (_data: {order_id: number}): Promise<{id: number, doc_name: string, document: {name: string, url: string, type: string, mime: string} }[]> => {
  return await listForexOrderFiles();
}

/* ---------------------------------------- also answered by core ---------- */

export const updateProducts = async (data: BookingFormPayload) => {
  return await decideForexLines({
    status: data.status.map((line) => ({
      id: line.id,
      status: line.status,
      reason_for_reject: line.reason_for_reject,
    })),
  });
};

/* ------------------------------------------- not available yet --------- */

export const getGenericOrderDetails = async (orderId: string): Promise<OrderDetailResponse> => {
  return await loadGenericOrder(orderId);
}

export const updateGenericOrderStatus = async (data: { order_id: number | string, status: string }): Promise<any> => {
  return await moveGenericOrder(data);
}

export const editGenericOrder = async (_data: {
  order_id: number | string;
  lead_id: number;
  assign_to: number;

}) => {
  return notAvailable("Linking a lead to a generic order");
}

export const updatePaymentStatus = async (data: PaymentStatusRequest) => {
  return await decideForexPayment({
    payments_id: data.payments_id,
    status: data.status,
    reason: data.reason,
  });
}

export const updateNotes = async ({ notes }: { notes: string }) => {
  return await setForexNotes(notes);
}

export const approveDocuments = async (data: ApproveDocumentReqType) => {
  return await verifyForexDocuments(data.document_list.filter((d) => d.status === "verified").map((d) => d.id));
}

export const updateTcs = async (data: UpdateTCSReqType) => {
  return await setForexTcs(data);
}

export const requestDocument = async (data: { docType: string; label?: string; requirementId?: string | null }): Promise<any> => {
  return await requestForexDocument(data);
}

/** Files the picked document(s) on the order. A re-upload adds a new document beside the old one. */
export const reuploadDocument = async (data: RequploadDocumentReqType & { requirementId?: string | null; replacesDocumentId?: string | null; requestId?: string | null }): Promise<any> => {
  const uploads: { file: string; type: string; name?: string }[] = [];
  if (data.doc) uploads.push({ file: data.doc, type: data.type, name: undefined });
  for (const extra of data.additional_doc ?? []) uploads.push({ file: extra.file, type: extra.name, name: extra.name });
  for (const item of uploads) {
    const safe = (item.name || item.type || "document").replace(/[^\w.\- ]+/g, "_");
    await uploadForexOrderDocument({
      file: fileFromDataUrl(item.file, `${safe}.pdf`),
      docType: item.type,
      requirementId: item.name ? undefined : data.requirementId,
      replacesDocumentId: item.name ? undefined : data.replacesDocumentId,
      requestId: item.name ? undefined : data.requestId,
    });
  }
}

/** Stores the invoice file on the order. Reading it automatically is not offered. */
export const uploadInvoices = async (data: {
  generic_order_details_id?: number;
  doc_id?: number;
  invoice: string;
  doc_uuid?: string;
  customers_id?: number;
}) => {
  return await uploadForexOrderDocument({ file: fileFromDataUrl(data.invoice, "invoice.pdf"), docType: "invoice" });
}

export const updateServiceCharges = async (data: { amount: number | null, reason: string }): Promise<any> => {
  return await setForexServiceCharge(data);
}

export const updateProductSingle = async (data: UpdateSingleProductReqType): Promise<any> => {
  // A card line's type is fixed by its product; only the amount can change.
  return await updateForexLine(data.uuid, { fxAmount: data.fx_amount });
}

export const updateRateAndQuantity = async (data: {
  uuid: string;
  fx_amount?: number;
  rate?: number;
  reason?: string;
}): Promise<any> => {
  return await updateForexLine(data.uuid, {
    fxAmount: data.fx_amount,
    rateOverride: data.rate,
    reason: data.reason,
  });
}

export const assignCard = async (data: {
  lineId: string;
  cardId?: string;
  cardNumber?: string;
  issuer?: string;
  expiresOn?: string;
}): Promise<any> => {
  return await assignForexCard(data);
}

export const getLinkedCards = async (customerId: string) => {
  return await listForexCards(customerId);
}

export const getCustomerAddresses = async (customerId: string) => {
  return await listCustomerAddresses(customerId);
}

export const approveKyc = async (data: {customers_id: string | number; reason: string; evidence: Partial<Record<"pan" | "passport", string>>}) => {
  return await approveForexKyc(String(data.customers_id), { reason: data.reason, evidence: data.evidence });
}

export const rejectDocument = async (data: { id: string; reason: string }) => {
  return await rejectForexDocument(data.id, data.reason);
}

export const getKycLogs = async (data: {customers_id: string | number}) => {
  return await getForexKycHistory(String(data.customers_id));
}

export const updateLrsStatus = async (_data: {order_id: number}): Promise<void> => {
  await markLrsSigned();
}
